import React, { useMemo } from 'react';
import {
  Alert,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type {
  CreateDiscountRuleInput,
  DiscountActionType,
  DiscountRuleDto,
  FanTierDto,
} from '@shared/api/hooks/useDiscountRules';
import { describeRule } from './ruleSummary';

const { Text } = Typography;

export interface RuleFormValues {
  name: string;
  description?: string;
  trigger: 'AUTOMATIC' | 'CODE';
  code?: string;
  window?: [Dayjs, Dayjs];
  categories?: string[];
  skus?: string;
  cartSubtotalMin?: number;
  cartQtyMin?: number;
  targetMinQty?: number;
  firstPurchase?: boolean;
  fulfillmentMethod?: 'IN_STORE_PICKUP' | 'SHIPPING' | 'ANY';
  actionType: DiscountActionType;
  actionValue?: number;
  actionMaxDiscount?: number;
  targetScope: 'CART' | 'MATCHED_ITEMS';
  bxgyBuyQty?: number;
  bxgyGetQty?: number;
  priority: number;
  stackable: boolean;
  exclusive: boolean;
  maxRedemptionsTotal?: number;
  maxRedemptionsPerUser?: number;
  perUserPeriod: 'ALL_TIME' | 'MONTH' | 'SEASON';
  isActive: boolean;
}

export const DEFAULT_RULE_VALUES: RuleFormValues = {
  name: '',
  trigger: 'AUTOMATIC',
  actionType: 'PERCENTAGE',
  targetScope: 'CART',
  // Default deliberatamente "innocui": una regola creata senza pensarci cumula
  // con le altre e non blocca nessuno.
  priority: 100,
  stackable: true,
  exclusive: false,
  perUserPeriod: 'ALL_TIME',
  isActive: true,
};

const CATEGORIES = ['JERSEY', 'SCARF', 'HAT', 'OTHER'];

export function toInput(v: RuleFormValues): CreateDiscountRuleInput {
  const skus = v.skus
    ? v.skus.split(',').map((s) => s.trim()).filter(Boolean)
    : undefined;
  return {
    name: v.name,
    description: v.description || undefined,
    trigger: v.trigger,
    code: v.trigger === 'CODE' ? v.code : undefined,
    startsAt: v.window?.[0]?.toISOString(),
    endsAt: v.window?.[1]?.toISOString(),
    conditions: {
      cartSubtotalMinCents: v.cartSubtotalMin ? Math.round(v.cartSubtotalMin * 100) : undefined,
      cartQtyMin: v.cartQtyMin || undefined,
      categories: v.categories?.length ? v.categories : undefined,
      skus: skus?.length ? skus : undefined,
      targetMinQty: v.targetMinQty || undefined,
      firstPurchase: v.firstPurchase || undefined,
      fulfillmentMethod:
        v.fulfillmentMethod && v.fulfillmentMethod !== 'ANY' ? v.fulfillmentMethod : undefined,
    },
    actionType: v.actionType,
    actionValue: v.actionValue,
    actionMaxDiscount: v.actionType === 'PERCENTAGE' ? v.actionMaxDiscount : undefined,
    targetScope: v.targetScope,
    bxgyBuyQty: v.actionType === 'BXGY' ? v.bxgyBuyQty : undefined,
    bxgyGetQty: v.actionType === 'BXGY' ? v.bxgyGetQty : undefined,
    priority: v.priority,
    stackable: v.stackable,
    exclusive: v.exclusive,
    maxRedemptionsTotal: v.maxRedemptionsTotal || undefined,
    maxRedemptionsPerUser: v.maxRedemptionsPerUser || undefined,
    perUserPeriod: v.perUserPeriod,
    isActive: v.isActive,
  };
}

export function fromRule(r: DiscountRuleDto): RuleFormValues {
  const c = r.conditions ?? {};
  return {
    name: r.name,
    description: r.description ?? undefined,
    trigger: r.trigger,
    code: r.code ?? undefined,
    window: r.startsAt && r.endsAt ? [dayjs(r.startsAt), dayjs(r.endsAt)] : undefined,
    categories: c.categories,
    skus: c.skus?.join(', '),
    cartSubtotalMin: c.cartSubtotalMinCents ? c.cartSubtotalMinCents / 100 : undefined,
    cartQtyMin: c.cartQtyMin,
    targetMinQty: c.targetMinQty,
    firstPurchase: c.firstPurchase,
    fulfillmentMethod: c.fulfillmentMethod ?? 'ANY',
    actionType: r.actionType,
    actionValue: r.actionValue === null ? undefined : Number(r.actionValue),
    actionMaxDiscount: r.actionMaxDiscount === null ? undefined : Number(r.actionMaxDiscount),
    targetScope: r.targetScope,
    bxgyBuyQty: r.bxgyBuyQty ?? undefined,
    bxgyGetQty: r.bxgyGetQty ?? undefined,
    priority: r.priority,
    stackable: r.stackable,
    exclusive: r.exclusive,
    maxRedemptionsTotal: r.maxRedemptionsTotal ?? undefined,
    maxRedemptionsPerUser: r.maxRedemptionsPerUser ?? undefined,
    perUserPeriod: r.perUserPeriod,
    isActive: r.isActive,
  };
}

/** Anteprima in italiano costruita dai valori correnti del form. */
function previewFrom(v: Partial<RuleFormValues>, tiers: FanTierDto[]): string {
  const fake = {
    ...DEFAULT_RULE_VALUES,
    ...v,
  } as RuleFormValues;
  const input = toInput(fake);
  return describeRule(
    {
      id: 'preview',
      name: input.name,
      description: input.description ?? null,
      isActive: input.isActive ?? true,
      trigger: input.trigger ?? 'AUTOMATIC',
      code: input.code ?? null,
      startsAt: input.startsAt ?? null,
      endsAt: input.endsAt ?? null,
      conditions: input.conditions ?? {},
      actionType: input.actionType,
      actionValue: input.actionValue?.toString() ?? null,
      actionMaxDiscount: input.actionMaxDiscount?.toString() ?? null,
      targetScope: input.targetScope ?? 'CART',
      bxgyBuyQty: input.bxgyBuyQty ?? null,
      bxgyGetQty: input.bxgyGetQty ?? null,
      priority: input.priority ?? 100,
      stackable: input.stackable ?? true,
      exclusive: input.exclusive ?? false,
      maxRedemptionsTotal: input.maxRedemptionsTotal ?? null,
      maxRedemptionsPerUser: input.maxRedemptionsPerUser ?? null,
      perUserPeriod: input.perUserPeriod ?? 'ALL_TIME',
      createdAt: new Date().toISOString(),
      tiers: [],
    },
    tiers,
  );
}

interface Props {
  form: ReturnType<typeof Form.useForm<RuleFormValues>>[0];
  tiers: FanTierDto[];
  isEdit: boolean;
}

export function DiscountRuleForm({ form, tiers, isEdit }: Props): React.ReactElement {
  const values = Form.useWatch([], form) as RuleFormValues | undefined;
  const actionType = values?.actionType ?? 'PERCENTAGE';
  const preview = useMemo(() => previewFrom(values ?? {}, tiers), [values, tiers]);

  const hasSelector = Boolean(values?.categories?.length || values?.skus?.trim());
  const needsSelector =
    values?.targetScope === 'MATCHED_ITEMS' || actionType === 'FIXED_PRICE' || actionType === 'BXGY';

  return (
    <Form form={form} layout="vertical" initialValues={DEFAULT_RULE_VALUES}>
      {/* L'anteprima e' sempre in cima: e' la difesa migliore contro una regola
          scritta male, molto piu' di otto campi letti separatamente. */}
      <Alert
        type="info"
        showIcon
        message="Anteprima"
        description={<Text strong>{preview}</Text>}
        style={{ marginBottom: 16 }}
      />

      <Row gutter={12}>
        <Col span={14}>
          <Form.Item name="name" label="Nome" rules={[{ required: true, min: 3, max: 120 }]}>
            <Input placeholder="Sconto Oro settembre" />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item name="trigger" label="Attivazione">
            <Select
              disabled={isEdit}
              options={[
                { value: 'AUTOMATIC', label: 'Automatica (si applica da sola)' },
                { value: 'CODE', label: 'Con codice inserito dal tifoso' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {values?.trigger === 'CODE' && (
        <Form.Item
          name="code"
          label="Codice"
          rules={[
            { required: true },
            { pattern: /^[A-Za-z0-9_-]{3,30}$/, message: '3-30 caratteri: lettere, cifre, - e _' },
          ]}
          extra="Il codice non e modificabile dopo la creazione."
        >
          <Input disabled={isEdit} placeholder="SALDI2026" />
        </Form.Item>
      )}

      <Form.Item name="description" label="Descrizione (mostrata al tifoso)">
        <Input.TextArea rows={2} maxLength={500} />
      </Form.Item>

      <Divider orientation="left">Condizioni</Divider>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="categories" label="Categorie">
            <Select
              mode="multiple"
              allowClear
              placeholder="Tutte"
              options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="skus" label="SKU (separati da virgola)">
            <Input placeholder="MAGLIA-HOME-26, SCIARPA-01" />
          </Form.Item>
        </Col>
      </Row>
      {needsSelector && !hasSelector && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Servono dei selettori"
          description="Con questa azione (o con 'solo gli articoli selezionati') devi indicare almeno una categoria o uno SKU: altrimenti non c'e nulla da scontare."
        />
      )}
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="cartSubtotalMin" label="Carrello minimo (EUR)">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="cartQtyMin" label="Articoli minimi">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="targetMinQty"
            label="Pezzi minimi fra i selezionati"
            tooltip="Conta solo gli articoli che corrispondono a categorie/SKU, non il carrello intero."
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="firstPurchase" valuePropName="checked">
            <Checkbox>Solo al primo acquisto</Checkbox>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="fulfillmentMethod" label="Modalita di consegna">
            <Select
              options={[
                { value: 'ANY', label: 'Qualsiasi' },
                { value: 'IN_STORE_PICKUP', label: 'Solo ritiro allo stadio' },
                { value: 'SHIPPING', label: 'Solo spedizione' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="window" label="Finestra di validita">
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Azione</Divider>
      <Row gutter={12}>
        <Col span={10}>
          <Form.Item name="actionType" label="Tipo di sconto">
            <Select
              options={[
                { value: 'PERCENTAGE', label: 'Percentuale' },
                { value: 'FIXED_AMOUNT', label: 'Importo fisso' },
                { value: 'FIXED_PRICE', label: 'Prezzo fisso per articolo' },
                { value: 'FREE_SHIPPING', label: 'Spedizione gratuita' },
                { value: 'BXGY', label: 'Prendi N paghi M' },
              ]}
            />
          </Form.Item>
        </Col>
        {actionType !== 'FREE_SHIPPING' && actionType !== 'BXGY' && (
          <Col span={7}>
            <Form.Item
              name="actionValue"
              label={actionType === 'PERCENTAGE' ? 'Percentuale (%)' : 'Importo (EUR)'}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0.01}
                max={actionType === 'PERCENTAGE' ? 100 : 99999}
                precision={2}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        )}
        {actionType === 'PERCENTAGE' && (
          <Col span={7}>
            <Form.Item name="actionMaxDiscount" label="Sconto massimo (EUR)">
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        )}
        {actionType === 'BXGY' && (
          <>
            <Col span={7}>
              <Form.Item name="bxgyBuyQty" label="Ne compri" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                name="bxgyGetQty"
                label="Ne ricevi gratis"
                rules={[{ required: true }]}
                tooltip="Vengono regalati sempre i pezzi piu economici fra quelli selezionati."
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
      {actionType !== 'FREE_SHIPPING' && (
        <Form.Item name="targetScope" label="Su cosa si applica">
          <Select
            options={[
              { value: 'CART', label: 'Su tutto il carrello' },
              { value: 'MATCHED_ITEMS', label: 'Solo sugli articoli selezionati' },
            ]}
          />
        </Form.Item>
      )}

      <Divider orientation="left">Limiti e combinabilita</Divider>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="maxRedemptionsPerUser" label="Max utilizzi per tifoso">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="illimitato" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="perUserPeriod" label="Periodo del limite">
            <Select
              options={[
                { value: 'ALL_TIME', label: 'In totale (mai piu di cosi)' },
                { value: 'MONTH', label: 'Al mese' },
                { value: 'SEASON', label: 'A stagione' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="maxRedemptionsTotal" label="Max utilizzi complessivi">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="illimitato" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item
            name="priority"
            label="Priorita"
            tooltip="Piu basso = valutata prima. A parita conta la data di creazione."
          >
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="stackable"
            label="Cumulabile"
            valuePropName="checked"
            extra="Se disattivo, non si somma a uno sconto gia applicato."
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="exclusive"
            label="Esclusiva"
            valuePropName="checked"
            extra="Se attiva e si applica, blocca tutte le regole successive."
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="isActive" label="Attiva" valuePropName="checked">
        <Switch />
      </Form.Item>
    </Form>
  );
}
